import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import ErrorBoundary from '../components/ErrorBoundary';
import { useCurrency } from '../contexts/CurrencyContext';

const CartContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 2rem;
  color: #fff;
`;

const CartGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 30px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ProductsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CartItem = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 20px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
  }
`;

const ProductImage = styled.div`
  width: 120px;
  height: 160px;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1a1a1a;
  position: relative;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: cover;
    transition: opacity 0.3s ease;
    opacity: ${props => props.isLoaded ? '1' : '0'};
    border-radius: 6px;
  }
  
  @media (max-width: 768px) {
    width: 90px;
    height: 120px;
  }
`;

const GiftCardImage = styled(ProductImage)`
  width: 120px;
  height: 114px;
  
  img {
    object-fit: contain;
    padding: 0;
    opacity: 1 !important;
  }
  
  @media (max-width: 768px) {
    width: 90px;
    height: 85px;
  }
`;

const ImagePlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1a1a1a;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`;

const ProductTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 500;
  color: #fff;
  margin: 0;
`;

const ProductDetails = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
`;

const ProductPrice = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: #fff;
  margin-top: auto;
`;

const ProductActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    grid-column: 1;
    flex-direction: row-reverse;
    margin-top: 10px;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 5px;
`;

const QuantityButton = styled.button`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityText = styled.span`
  font-size: 1rem;
  min-width: 30px;
  text-align: center;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #ff6b6b;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 5px;
  transition: color 0.2s;
  
  &:hover {
    color: #ff4757;
    text-decoration: underline;
  }
`;

const SummaryCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 25px;
`;

const SummaryTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: #fff;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
`;

const SummaryDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 15px 0;
`;

const TotalRow = styled(SummaryRow)`
  font-weight: 600;
  font-size: 1.2rem;
  color: #fff;
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(90deg, #A961ED 0%, #7838D5 100%);
  border: none;
  border-radius: 50px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  margin-top: 20px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: linear-gradient(90deg, #9050c9 0%, #6428b8 100%);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyCartMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
`;

const EmptyCartText = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 30px;
`;

const ShopButton = styled(Link)`
  display: inline-block;
  padding: 14px 30px;
  background: linear-gradient(90deg, #A961ED 0%, #7838D5 100%);
  border-radius: 50px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
  
  &:hover {
    background: linear-gradient(90deg, #9050c9 0%, #6428b8 100%);
  }
`;

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  // Track loaded images
  const [loadedImages, setLoadedImages] = useState({});

  // Load cart items from localStorage
  const loadCart = () => {
    try {
      const cartData = localStorage.getItem('cart');
      const items = cartData ? JSON.parse(cartData) : [];

      // Debug: Log the cart items structure (simplified)
      console.log('🛒 Cart items loaded:', items.length, 'items');

      setCartItems(items);
    } catch (error) {
      console.error('Error loading cart:', error);
      setCartItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load cart on component mount
  useEffect(() => {
    setIsLoading(true);
    loadCart();
  }, []);

  // Update quantity for a cart item
  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    // Check if we're increasing or decreasing quantity
    const currentItem = cartItems.find(item => item.id === itemId);

    // Update the cart items in state for immediate UI response
    const updatedCart = cartItems.map(item =>
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    );

    setCartItems(updatedCart);

    // Update cart in localStorage
    try {
      localStorage.setItem('cart', JSON.stringify(updatedCart));
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      console.error('Failed to update cart quantity:', error);
      alert('Failed to update cart: Storage quota exceeded.');

      // Revert the cart items to the previous state
      try {
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
          setCartItems(JSON.parse(savedCart));
        }
      } catch (revertError) {
        console.error('Error reverting cart:', revertError);
      }
    }
  };

  // Remove an item from the cart
  const removeItem = (itemId) => {
    // Immediately update UI for responsiveness
    const updatedCart = cartItems.filter(item => item.id !== itemId);
    setCartItems(updatedCart);

    // Update cart in localStorage
    try {
      localStorage.setItem('cart', JSON.stringify(updatedCart));
      // Notify other components of the change
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      console.error('Failed to remove item from cart:', error);
    }
  };

  // Calculate subtotal
  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => {
      return total + (parseFloat(item.price) * (item.quantity || 1));
    }, 0);
  };

  // Calculate shipping fee - 20 EUR if cart contains any printed items
  const calculateShipping = () => {
    if (!cartItems || cartItems.length === 0) {
      console.log('Cart shipping debug: No cart items');
      return 0;
    }

    console.log('Cart shipping debug: Cart items:', cartItems.map(item => ({
      name: item.name,
      printType: item.printType,
      type: item.type
    })));

    // Check if any item in cart is printed (not digital)
    const hasPhysicalItems = cartItems.some(item =>
      item.printType && item.printType !== 'digital'
    );

    console.log('Cart shipping debug: Has physical items:', hasPhysicalItems);

    // Return 20 EUR equivalent in selected currency
    return hasPhysicalItems ? 20.00 : 0;
  };

  // Calculate total including shipping
  const calculateTotal = () => {
    return calculateSubtotal() + calculateShipping();
  };

  // Handle checkout
  const handleCheckout = () => {
    navigate('/checkout');
  };

  // Handle image load
  const handleImageLoad = (itemId) => {
    setLoadedImages(prev => ({
      ...prev,
      [itemId]: true
    }));
  };

  // Handle image error
  const handleImageError = (itemId) => {
    setLoadedImages(prev => ({
      ...prev,
      [itemId]: 'error'
    }));
  };

  // Check if an item is a star map
  const isStarMap = (item) => {
    // Star maps usually have properties like date, location, mapStyle
    return item.date && item.location && !item.type;
  };

  // Helper function to format coordinates with proper spacing
  const formatCoordinates = (coordinates) => {
    if (!coordinates || typeof coordinates !== 'string') return coordinates;
    // Add space after comma if it doesn't exist
    return coordinates.replace(/,(?!\s)/g, ', ');
  };

  // Removed unused map preview functions (preview temporarily disabled)



  // Display loading state
  if (isLoading) {
    return (
      <CartContainer>
        <PageTitle>Your Cart</PageTitle>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          Loading your cart...
        </div>
      </CartContainer>
    );
  }

  // Display empty cart message if cart is empty
  if (cartItems.length === 0) {
    return (
      <CartContainer>
        <PageTitle>Your Cart</PageTitle>
        <EmptyCartMessage>
          <EmptyCartText>Your cart is empty. Start adding products!</EmptyCartText>
          <ShopButton to="/create">Create Your Star Map</ShopButton>
        </EmptyCartMessage>
      </CartContainer>
    );
  }

  // Render cart with items
  return (
    <CartContainer>
      <PageTitle>Your Cart</PageTitle>

      <CartGrid>
        {/* Products List */}
        <ProductsList>
          {cartItems.map(item => (
            <CartItem key={item.id}>
              <ProductInfo>
                <ProductTitle>{item.name}</ProductTitle>
                <ProductDetails>
                  {item.date && <div>Date: {item.date}</div>}
                  {(item.locationName || item.location) && <div>Location: {item.locationName || formatCoordinates(item.location)}</div>}
                  {item.size && <div>Size: {item.size}</div>}
                  {item.frame && <div>Frame: {item.frame}</div>}
                  {item.printType && <div>Type: {item.printType}</div>}
                </ProductDetails>
                <ProductPrice>{formatPrice(parseFloat(item.price))}</ProductPrice>
              </ProductInfo>

              <ProductActions>
                <QuantityControl>
                  <QuantityButton
                    onClick={() => updateQuantity(item.id, (item.quantity || 1) - 1)}
                    disabled={(item.quantity || 1) <= 1}
                  >
                    -
                  </QuantityButton>
                  <QuantityText>{item.quantity || 1}</QuantityText>
                  <QuantityButton onClick={() => updateQuantity(item.id, (item.quantity || 1) + 1)}>
                    +
                  </QuantityButton>
                </QuantityControl>

                <RemoveButton onClick={() => removeItem(item.id)}>
                  Remove
                </RemoveButton>
              </ProductActions>
            </CartItem>
          ))}
        </ProductsList>

        {/* Order Summary */}
        <SummaryCard>
          <SummaryTitle>Order Summary</SummaryTitle>

          <SummaryRow>
            <div>Subtotal</div>
            <div>{formatPrice(calculateSubtotal())}</div>
          </SummaryRow>

          {calculateShipping() > 0 && (
            <SummaryRow>
              <div>Shipping</div>
              <div>{formatPrice(calculateShipping())}</div>
            </SummaryRow>
          )}

          <SummaryDivider />

          <TotalRow>
            <div>Total</div>
            <div>{formatPrice(calculateTotal())}</div>
          </TotalRow>

          <CheckoutButton onClick={handleCheckout}>
            Proceed to Checkout
          </CheckoutButton>
        </SummaryCard>
      </CartGrid>
    </CartContainer>
  );
};

const WrappedCartPage = () => (
  <ErrorBoundary>
    <CartPage />
  </ErrorBoundary>
);

export default WrappedCartPage; 